import { useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, API_BASE } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";
import type { MeResponse } from "../../lib/auth";

interface OutletCtx {
  me: MeResponse;
  refreshMe: () => Promise<void>;
}

export default function SettingsPage() {
  const { me } = useOutletContext<OutletCtx>();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  async function handlePortal() {
    setLoading(true);
    try {
      const { url } = await api.post<{ url: string }>("/api/billing/portal");
      window.location.href = url;
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">
          Manage your account, connected social accounts, AI keys, and posting
          preferences.
        </p>
      </div>

      <section className="card">
        <h2 className="font-semibold text-ink-900">Account</h2>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-ink-500">Email</dt>
            <dd className="truncate text-ink-900">
              {me.user.email_is_placeholder ? (
                <span className="text-ink-500 italic">No email on file</span>
              ) : (
                me.user.email
              )}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-500">Member since</dt>
            <dd className="text-ink-900">
              {new Date(me.user.created_at).toLocaleDateString()}
            </dd>
          </div>
        </dl>
      </section>

      {me.user.x_user_id ? (
        <section className="card">
          <h2 className="font-semibold text-ink-900">X account</h2>
          <p className="mt-1 text-sm text-ink-500">
            Signed in via Sign in with X. This is your login identity, not a
            posting account — to publish posts, also connect your X dev keys
            below.
          </p>
          <div className="mt-4 flex items-center gap-4">
            {me.user.x_avatar_url ? (
              <img
                src={me.user.x_avatar_url.replace("_normal.", "_bigger.")}
                alt={me.user.x_username ?? "X avatar"}
                className="h-14 w-14 rounded-full bg-ink-100"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ink-100 text-lg font-medium text-ink-500">
                {(me.user.x_name ?? me.user.x_username ?? "X")
                  .slice(0, 1)
                  .toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-ink-900">
                {me.user.x_name ?? me.user.x_username}
              </p>
              {me.user.x_username ? (
                <a
                  href={`https://x.com/${me.user.x_username}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-ink-500 hover:underline"
                >
                  @{me.user.x_username}
                </a>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <section className="card">
        <h2 className="font-semibold text-ink-900">Subscription</h2>
        <dl className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-ink-500">Plan</dt>
            <dd className="text-ink-900">{me.subscription.plan}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-500">Status</dt>
            <dd className="text-ink-900">{me.subscription.status}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-500">Generations this month</dt>
            <dd className="text-ink-900 tabular-nums">
              {me.credits.remaining}/{me.credits.quota} left
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-500">Resets</dt>
            <dd className="text-ink-900">
              {new Date(me.credits.resets_at).toLocaleDateString()}
            </dd>
          </div>
        </dl>
        {me.subscription.plan !== "free" ? (
          <button
            onClick={handlePortal}
            disabled={loading}
            className="btn-secondary mt-4"
          >
            {loading ? "Opening…" : "Manage in Stripe"}
          </button>
        ) : (
          <a href="/pricing" className="btn-primary mt-4 inline-flex no-underline">
            Upgrade
          </a>
        )}
      </section>

      <BrandLogoSection />

      <SocialAccountsSection />

      <DangerZoneSection />
    </div>
  );
}

function BrandLogoSection() {
  const toast = useToast();
  const confirm = useConfirm();
  const { me, refreshMe } = useOutletContext<OutletCtx>();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [bust, setBust] = useState(0);

  const logoUrl = me.user.logo_url
    ? `${API_BASE}${me.user.logo_url}?v=${bust}`
    : null;

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API_BASE}/api/me/logo`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as
          | { error?: string; message?: string }
          | null;
        throw new Error(data?.message ?? data?.error ?? "Upload failed");
      }
      toast.success("Logo updated.");
      setBust(Date.now());
      await refreshMe?.();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  async function remove() {
    const ok = await confirm({
      title: "Remove brand logo",
      message: "Your brand logo will be deleted from your profile.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    setRemoving(true);
    try {
      await api.del("/api/me/logo");
      toast.success("Logo removed.");
      await refreshMe?.();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setRemoving(false);
    }
  }

  return (
    <section className="card">
      <h2 className="font-semibold text-ink-900">Brand logo</h2>
      <p className="mt-1 text-sm text-ink-500">
        Optional. Appears in the corner of every visual you generate.
        PNG / JPG / WebP / SVG, up to 2 MB. Square works best.
      </p>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg bg-ink-100">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Brand logo"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="text-xs text-ink-500">no logo</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="btn-secondary text-sm">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={onPick}
              disabled={uploading}
              className="hidden"
            />
            {uploading ? "Uploading…" : logoUrl ? "Replace" : "Upload"}
          </label>
          {logoUrl ? (
            <button
              onClick={remove}
              className="btn-secondary text-sm"
              disabled={removing}
              type="button"
            >
              {removing ? "Removing…" : "Remove"}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

interface SocialAccount {
  id: string;
  platform: string;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  status: string;
  created_at: number;
}

type ConnectMode = null | "bluesky" | "twitter" | "linkedin";

function SocialAccountsSection() {
  const toast = useToast();
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [connectMode, setConnectMode] = useState<ConnectMode>(null);
  const [identifier, setIdentifier] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [twitterCk, setTwitterCk] = useState("");
  const [twitterCs, setTwitterCs] = useState("");
  const [twitterAt, setTwitterAt] = useState("");
  const [twitterAts, setTwitterAts] = useState("");
  const [linkedinClientId, setLinkedinClientId] = useState("");
  const [linkedinClientSecret, setLinkedinClientSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("linkedin_connected") === "1") {
      toast.success("LinkedIn account connected.");
      window.history.replaceState({}, "", "/app/settings");
      void load();
    } else if (params.get("linkedin_error")) {
      const err = params.get("linkedin_error") || "";
      toast.error(`LinkedIn connect failed: ${err.slice(0, 200)}`);
      window.history.replaceState({}, "", "/app/settings");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    setLoadingAccounts(true);
    try {
      const data = await api.get<{ social_accounts: SocialAccount[] }>(
        "/api/social-accounts"
      );
      setAccounts(data.social_accounts ?? []);
    } catch {
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function connectBluesky(e: React.FormEvent) {
    e.preventDefault();
    if (!identifier.trim() || !appPassword.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/api/social-accounts/bluesky", {
        identifier: identifier.trim(),
        app_password: appPassword.trim(),
      });
      toast.success("Bluesky account connected.");
      setIdentifier("");
      setAppPassword("");
      setConnectMode(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function connectTwitter(e: React.FormEvent) {
    e.preventDefault();
    if (
      !twitterCk.trim() ||
      !twitterCs.trim() ||
      !twitterAt.trim() ||
      !twitterAts.trim()
    )
      return;
    setSubmitting(true);
    try {
      await api.post("/api/social-accounts/twitter", {
        consumer_key: twitterCk.trim(),
        consumer_secret: twitterCs.trim(),
        access_token: twitterAt.trim(),
        access_token_secret: twitterAts.trim(),
      });
      toast.success("X account connected.");
      setTwitterCk("");
      setTwitterCs("");
      setTwitterAt("");
      setTwitterAts("");
      setConnectMode(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function startLinkedinOAuth(e: React.FormEvent) {
    e.preventDefault();
    if (!linkedinClientId.trim() || !linkedinClientSecret.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post<{ authorize_url: string }>(
        "/api/social-accounts/linkedin/start",
        {
          client_id: linkedinClientId.trim(),
          client_secret: linkedinClientSecret.trim(),
        }
      );
      window.location.href = res.authorize_url;
    } catch (err) {
      toast.error(apiErrorMessage(err));
      setSubmitting(false);
    }
  }

  async function disconnect(id: string, handle: string) {
    const ok = await confirm({
      title: `Disconnect @${handle}`,
      message: "Scheduled posts using this account will fail until you reconnect.",
      confirmLabel: "Disconnect",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/social-accounts/${id}`);
      toast.success("Disconnected.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  return (
    <section className="card">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-ink-900">Social accounts</h2>
        {connectMode === null ? (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setConnectMode("bluesky")}
              className="btn-secondary text-sm"
            >
              + Bluesky
            </button>
            <button
              onClick={() => setConnectMode("twitter")}
              className="btn-secondary text-sm"
            >
              + X / Twitter
            </button>
            <button
              onClick={() => setConnectMode("linkedin")}
              className="btn-secondary text-sm"
            >
              + LinkedIn
            </button>
          </div>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-ink-500">
        Connect platforms to schedule and publish variants directly from
        BrandEcho.
      </p>

      {loadingAccounts ? (
        <p className="mt-3 text-sm text-ink-500">Loading…</p>
      ) : accounts.length > 0 ? (
        <ul className="mt-4 divide-y divide-ink-100">
          {accounts.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between py-3 text-sm"
            >
              <div>
                <p className="font-medium text-ink-900">
                  {a.display_name ?? a.handle}
                </p>
                <p className="text-xs text-ink-500">
                  {a.platform} · @{a.handle}
                  {a.status !== "active" ? ` · ${a.status}` : ""}
                </p>
              </div>
              <button
                onClick={() => disconnect(a.id, a.handle)}
                className="text-xs text-red-600 hover:underline"
              >
                Disconnect
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-ink-500">
          No accounts connected yet.
        </p>
      )}

      {connectMode === "bluesky" ? (
        <form onSubmit={connectBluesky} className="mt-5 space-y-3 rounded-lg bg-ink-50 p-4">
          <h3 className="text-sm font-medium text-ink-900">Connect Bluesky</h3>
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            Use an <strong>App Password</strong>, not your main Bluesky password.
            Create one at{" "}
            <a
              href="https://bsky.app/settings/app-passwords"
              target="_blank"
              rel="noreferrer"
              className="font-medium"
            >
              bsky.app/settings/app-passwords
            </a>
            . The password is encrypted at rest.
          </div>
          <div>
            <label className="mb-1 block text-xs text-ink-500">
              Handle (e.g. yourname.bsky.social)
            </label>
            <input
              className="input text-sm"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="yourname.bsky.social"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-ink-500">
              App Password
            </label>
            <input
              className="input text-sm"
              type="password"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              placeholder="xxxx-xxxx-xxxx-xxxx"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary text-sm"
              disabled={submitting}
            >
              {submitting ? "Connecting…" : "Connect"}
            </button>
            <button
              type="button"
              onClick={() => setConnectMode(null)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {connectMode === "twitter" ? (
        <form onSubmit={connectTwitter} className="mt-5 space-y-3 rounded-lg bg-ink-50 p-4">
          <h3 className="text-sm font-medium text-ink-900">Connect X / Twitter</h3>
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            Open{" "}
            <a
              href="https://developer.x.com/en/portal/projects-and-apps"
              target="_blank"
              rel="noreferrer"
              className="font-medium"
            >
              your X developer app
            </a>{" "}
            → set <strong>App permissions</strong> to <strong>Read and write</strong> →
            regenerate <strong>API Key + Secret</strong> and{" "}
            <strong>Access Token + Secret</strong>. All 4 are encrypted at rest. We
            never share your keys; each user uses their own X dev app so your rate
            limits stay yours.
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-ink-500">
                API Key (Consumer Key)
              </label>
              <input
                className="input font-mono text-xs"
                type="password"
                value={twitterCk}
                onChange={(e) => setTwitterCk(e.target.value)}
                placeholder="abc...xyz"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ink-500">
                API Key Secret
              </label>
              <input
                className="input font-mono text-xs"
                type="password"
                value={twitterCs}
                onChange={(e) => setTwitterCs(e.target.value)}
                placeholder="abc...xyz"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ink-500">
                Access Token
              </label>
              <input
                className="input font-mono text-xs"
                type="password"
                value={twitterAt}
                onChange={(e) => setTwitterAt(e.target.value)}
                placeholder="123-abc...xyz"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-ink-500">
                Access Token Secret
              </label>
              <input
                className="input font-mono text-xs"
                type="password"
                value={twitterAts}
                onChange={(e) => setTwitterAts(e.target.value)}
                placeholder="abc...xyz"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary text-sm"
              disabled={submitting}
            >
              {submitting ? "Connecting…" : "Connect"}
            </button>
            <button
              type="button"
              onClick={() => setConnectMode(null)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {connectMode === "linkedin" ? (
        <form onSubmit={startLinkedinOAuth} className="mt-5 space-y-3 rounded-lg bg-ink-50 p-4">
          <h3 className="text-sm font-medium text-ink-900">Connect LinkedIn</h3>
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            Open{" "}
            <a
              href="https://www.linkedin.com/developers/apps"
              target="_blank"
              rel="noreferrer"
              className="font-medium"
            >
              your LinkedIn developer app
            </a>
            . Under <strong>Products</strong> request access to{" "}
            <strong>Sign In with LinkedIn (OIDC)</strong> +{" "}
            <strong>Share on LinkedIn</strong> (both grant instantly). Under{" "}
            <strong>Auth</strong> add this redirect URL exactly:
            <code className="mt-2 block break-all rounded bg-white p-2 text-[10px] text-ink-900">
              https://brandecho-api.yjh-e5c.workers.dev/api/auth/linkedin/callback
            </code>
            Then paste the Client ID + Client Secret below — we'll redirect you to
            LinkedIn to authorize.
          </div>
          <div>
            <label className="mb-1 block text-xs text-ink-500">Client ID</label>
            <input
              className="input font-mono text-xs"
              type="text"
              value={linkedinClientId}
              onChange={(e) => setLinkedinClientId(e.target.value)}
              placeholder="86xxxxxxxxxxxx"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-ink-500">Client Secret</label>
            <input
              className="input font-mono text-xs"
              type="password"
              value={linkedinClientSecret}
              onChange={(e) => setLinkedinClientSecret(e.target.value)}
              placeholder="WPL_AP1...."
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary text-sm"
              disabled={submitting}
            >
              {submitting ? "Redirecting…" : "Authorize on LinkedIn →"}
            </button>
            <button
              type="button"
              onClick={() => setConnectMode(null)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}



function DangerZoneSection() {
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const canDelete = confirmText === "DELETE" && password.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canDelete) return;
    setBusy(true);
    setErrorMsg("");
    try {
      await api.del("/api/me/account", {
        password,
        confirm: confirmText,
      });
      toast.success("Account deleted. Goodbye 👋");
      window.setTimeout(() => navigate("/", { replace: true }), 600);
    } catch (err) {
      const msg = apiErrorMessage(err);
      if (/INVALID_CREDENTIALS|wrong/i.test(msg)) {
        setErrorMsg("Wrong password. Try again.");
      } else {
        setErrorMsg(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card border-red-200/70 bg-red-50/40">
      <h2 className="font-semibold text-red-700">Danger zone</h2>
      <p className="mt-1 text-sm text-red-700/80">
        Permanently delete your account and all related data — voices, projects,
        drafts, scheduled posts, hooks, analytics, uploaded media. This cannot be
        undone.
      </p>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn-danger mt-4"
        >
          Delete my account…
        </button>
      ) : (
        <form
          onSubmit={submit}
          className="mt-4 space-y-3 rounded-xl border border-red-200/70 bg-white p-4"
        >
          <p className="text-sm text-ink-700">
            We will cancel your Stripe subscription, wipe your D1 rows, purge
            your R2 files, and sign you out. There is no recovery after this.
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">
              Confirm your password
            </label>
            <input
              type="password"
              required
              autoFocus
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">
              Type <code className="rounded bg-ink-100 px-1.5 py-0.5">DELETE</code>{" "}
              to confirm
            </label>
            <input
              type="text"
              className="input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
            />
          </div>
          {errorMsg ? (
            <p className="text-sm text-red-600">{errorMsg}</p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setPassword("");
                setConfirmText("");
                setErrorMsg("");
              }}
              className="btn-secondary"
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-danger"
              disabled={!canDelete || busy}
            >
              {busy ? "Deleting…" : "Delete account permanently"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
